import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { type InferType, object, string } from 'yup'

import { ChangePhotoPart } from '@/compoments/common/ChangePhotoPart'
import Input from '@/compoments/common/Input'
import { useUpdateMeMutation } from '@/services/auth'
import { ROLE_LABELS, type UserRoleEnum } from '@/types'
import { getMediaUrl } from '@/utils/helpers'

const schema = object({
    fullName: string().required('Full name is required'),
    email: string().email('Invalid email format').required('Email is required'),
    phone: string().required('Phone is required'),
    role: string(),
})

type FormType = InferType<typeof schema>

export const AccountSettings = () => {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormType>({
        mode: 'onChange',
        resolver: yupResolver(schema),
    })

    const [updateMe] = useUpdateMeMutation()
    const { user, activeRole } = useSelector((state: any) => state.auth)

    useEffect(() => {
        if (!user) return

        setValue('fullName', user.fullName)
        setValue('email', user.email)
        setValue('phone', user.phone)
        setValue(
            'role',
            ROLE_LABELS[activeRole as UserRoleEnum] || 'Dispatcher',
        )
    }, [user, activeRole])

    const [photo, setPhoto] = useState<File | null>(null)
    const [photoTouch, setPhotoTouch] = useState(false)

    const onSubmit = async (data: FormType) => {
        const formData = new FormData()

        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value)
        })

        if (photoTouch && photo) {
            formData.append('photo', photo, photo.name)
        }

        try {
            const res = await updateMe(formData)
            console.log(res, 'res')

            if (res?.data?.success) {
                toast.success('Updated successfully!')
            } else {
                toast.error(res?.data?.message || 'Something went wrong')
            }
        } catch (error) {
            toast.error('Something went wrong')
            console.error('rejected', error)
        }
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newPhoneValue = e.target.value

        if (newPhoneValue.startsWith('+')) {
            newPhoneValue = '+' + newPhoneValue.slice(1).replace(/\D/g, '')
        } else {
            newPhoneValue = newPhoneValue.replace(/\D/g, '')
        }

        setValue('phone', newPhoneValue)
    }

    return (
        <div className="settings-body">
            <div className="settings__header ">
                <h4 className="mb-22">Account Information</h4>
                <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    className="btn btn--primary-gradient btn--lg fullRadius mb-22"
                >
                    <span className="text--md">Save Changes</span>
                </button>
            </div>

            <div className="row gy-42">
                <div className="col-lg-5">
                    <h5 className="mb-13">Profile Picture</h5>
                    <p className="text--xs color-text-color-1">
                        Edit your profile picture
                    </p>
                </div>
                <div className="col-lg-7">
                    <ChangePhotoPart
                        onChange={val => {
                            setPhoto(val as File)
                            setPhotoTouch(true)
                        }}
                        userPhoto={user?.photo ? getMediaUrl(user.photo) : null}
                    />
                </div>
                <div className="col-lg-5">
                    <h5 className="mb-13">General Info</h5>
                    <p className="text--xs color-text-color-1">
                        Edit information about your personal data
                    </p>
                </div>
                <div className="col-lg-7">
                    <div className="form-group--row input--lg mb-22">
                        <Input
                            label="Name"
                            className="input input--outline  w-full"
                            type="text"
                            placeholder=""
                            error={errors.fullName?.message}
                            {...register('fullName')}
                        />
                    </div>
                    <div className="form-group--row input--lg mb-22">
                        <label className="label">Role</label>
                        <input
                            disabled
                            className="input input--outline  w-full"
                            type="text"
                            placeholder=""
                            {...register('role')}
                        />
                    </div>
                    <div className="form-group--row input--lg mb-22">
                        <Input
                            label="Email"
                            className="input input--outline  w-full"
                            type="text"
                            placeholder=""
                            error={errors.email?.message}
                            {...register('email')}
                        />
                    </div>
                    <div className="form-group--row input--lg">
                        <Input
                            label="Phone Number"
                            className="input input--outline  w-full"
                            type="text"
                            placeholder=""
                            error={errors.phone?.message}
                            {...register('phone')}
                            onChange={handlePhoneChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
